/**
 * Deliverables — every finished thing the agents have made.
 *
 * Follows Content Hub's shape because that is the pattern people already know
 * here: filter rail, search, paginated gallery. The list is keyset-paginated,
 * so it behaves the same at 20 items and at 20,000.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useInfiniteQuery } from '@tanstack/react-query';
import {
  FileText, Image as ImageIcon, LayoutDashboard, Presentation,
  Send, Table2, File, Search, Loader2, ExternalLink, Play,
} from 'lucide-react';
import { cn } from '@/components/ui/utils';
import { coworkApi, API_BASE_URL, type Deliverable, type DeliverableKind } from '@/cowork/omniad/api/coworkApi';
import { isCoworkLive } from '@/cowork/live/coworkClient';
import { useActiveBrand } from '@/cowork/useActiveBrand';
import { PresentationModal } from '@/cowork/omniad/components/PresentationModal';

const KINDS: Array<{ id: DeliverableKind | 'all'; label: string; icon: typeof FileText }> = [
  { id: 'all', label: 'Everything', icon: File },
  { id: 'presentation', label: 'Presentations', icon: Presentation },
  { id: 'document', label: 'Documents', icon: FileText },
  { id: 'image', label: 'Images', icon: ImageIcon },
  { id: 'dashboard', label: 'Dashboards', icon: LayoutDashboard },
  { id: 'report', label: 'Reports', icon: Table2 },
  { id: 'post', label: 'Posts', icon: Send },
];

const KIND_STYLE: Record<string, { bg: string; fg: string }> = {
  presentation: { bg: '#f4ecff', fg: '#7c3aed' },
  document: { bg: '#eef4ff', fg: '#2563eb' },
  image: { bg: '#ecfdf5', fg: '#059669' },
  dashboard: { bg: '#fff7ed', fg: '#c2410c' },
  report: { bg: '#fdf2f8', fg: '#db2777' },
  post: { bg: '#eff6ff', fg: '#0369a1' },
  file: { bg: '#f3f4f6', fg: '#4b5563' },
};

function iconFor(kind: string) {
  return (KINDS.find((k) => k.id === kind)?.icon) ?? File;
}

function timeAgo(iso: string): string {
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days === 1) return 'Yesterday';
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}

function formatSize(bytes?: number): string | null {
  if (!bytes) return null;
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** Backend paths are relative to the cowork origin. */
function absolute(url?: string): string | undefined {
  if (!url) return undefined;
  return url.startsWith('http') ? url : `${API_BASE_URL}${url}`;
}

/**
 * How a deliverable opens.
 *
 * Only /assets/* is a plain URL. Everything under /v1/* needs a Bearer token,
 * which a target="_blank" navigation cannot send — so those rendered as links
 * returned 401 and, for presentations, would have shown raw JSON even if they
 * hadn't. Decks open in the viewer; anything else API-backed goes to the task
 * that produced it.
 */
function openMode(item: Deliverable): 'deck' | 'file' | 'task' | 'none' {
  if (item.kind === 'presentation' && presentationIdOf(item)) return 'deck';
  if (item.url?.startsWith('/assets/') || item.url?.startsWith('http')) return 'file';
  if (item.taskId) return 'task';
  return 'none';
}

/** Presentations are addressed as /v1/presentations/<id> whichever source indexed them. */
function presentationIdOf(item: Deliverable): string | null {
  const m = /\/v1\/presentations\/([^/?#]+)/.exec(item.url ?? '');
  return m ? m[1] : null;
}

function DeliverableCard({ item, onOpenDeck }: { item: Deliverable; onOpenDeck: (id: string) => void }) {
  const Icon = iconFor(item.kind);
  const style = KIND_STYLE[item.kind] ?? KIND_STYLE.file;
  const href = absolute(item.url);
  const thumb = absolute(item.thumbUrl);
  const size = formatSize(item.sizeBytes);
  const mode = openMode(item);

  const shell = 'group flex flex-col overflow-hidden rounded-[16px] border border-gray-100 bg-white text-left transition-all hover:border-[#be43ff]/30 hover:shadow-md';

  const body = (
    <>
      <div
        className="relative flex aspect-[4/3] items-center justify-center overflow-hidden"
        style={{ background: thumb ? '#f8f9fa' : style.bg }}
      >
        {thumb ? (
          <img src={thumb} alt="" className="h-full w-full object-cover" loading="lazy" />
        ) : (
          <Icon className="h-8 w-8" style={{ color: style.fg }} aria-hidden />
        )}
        {mode !== 'none' && (
          <span className="absolute right-2 top-2 flex h-6 w-6 items-center justify-center rounded-lg bg-white/90 opacity-0 shadow-sm transition-opacity group-hover:opacity-100">
            {mode === 'deck'
              ? <Play className="h-3 w-3 text-gray-500" aria-hidden />
              : <ExternalLink className="h-3 w-3 text-gray-500" aria-hidden />}
          </span>
        )}
      </div>

      <div className="flex flex-1 flex-col gap-1 p-3">
        <p className="line-clamp-2 text-[13px] leading-snug text-[#101828]" style={{ fontWeight: 600 }}>
          {item.title}
        </p>
        <div className="mt-auto flex items-center gap-1.5 pt-1 text-[11px] text-[#98a2b3]">
          <span
            className="rounded-full px-1.5 py-0.5"
            style={{ background: style.bg, color: style.fg }}
          >
            {item.kind}
          </span>
          {item.agentName && <span className="truncate">{item.agentName}</span>}
          <span className="ml-auto shrink-0">{size ?? timeAgo(item.createdAt)}</span>
        </div>
      </div>
    </>
  );

  if (mode === 'deck') {
    const id = presentationIdOf(item)!;
    return <button type="button" onClick={() => onOpenDeck(id)} className={shell}>{body}</button>;
  }
  if (mode === 'file') {
    return <a href={href} target="_blank" rel="noreferrer" className={shell}>{body}</a>;
  }
  if (mode === 'task') {
    return (
      <button
        type="button"
        onClick={() => window.dispatchEvent(new CustomEvent('agent-rail-navigate', { detail: '/agents/tasks' }))}
        className={shell}
      >
        {body}
      </button>
    );
  }
  return <div className={shell}>{body}</div>;
}

export function DeliverablesPage({ initialKind = 'all', heading = 'Deliverables', subheading = 'Everything your agents have produced: presentations, documents, images, dashboards and posts.' }: { initialKind?: DeliverableKind | 'all'; heading?: string; subheading?: string } = {}) {
  const [kind, setKind] = useState<DeliverableKind | 'all'>(initialKind);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const { brandId } = useActiveBrand();
  const sentinel = useRef<HTMLDivElement>(null);
  // ?campaign= scopes the library to one campaign — how the rail links in.
  const [openDeckId, setOpenDeckId] = useState<string | null>(null);
  const [params, setParams] = useSearchParams();
  const campaignId = params.get('campaign') || undefined;
  const navigate = useNavigate();

  // Debounce so typing doesn't fire a request per keystroke.
  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const query = useInfiniteQuery({
    queryKey: ['cowork', 'deliverables', kind, search, brandId, campaignId],
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      coworkApi.deliverables.list({
        kind: kind === 'all' ? undefined : kind,
        campaignId,
        search: search || undefined,
        cursor: pageParam,
        limit: 40,
      }),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    enabled: isCoworkLive(),
  });

  const { fetchNextPage, hasNextPage, isFetchingNextPage } = query;
  const items = useMemo(
    () => query.data?.pages.flatMap((p) => p.deliverables) ?? [],
    [query.data],
  );
  const counts = query.data?.pages[0]?.counts ?? {};
  const total = Object.values(counts).reduce((a, b) => a + b, 0);

  // Infinite scroll — load the next page when the sentinel comes into view.
  const observe = useCallback(() => {
    const el = sentinel.current;
    if (!el || !hasNextPage || isFetchingNextPage) return;
    const io = new IntersectionObserver((entries) => {
      if (entries[0]?.isIntersecting) void fetchNextPage();
    }, { rootMargin: '400px' });
    io.observe(el);
    return () => io.disconnect();
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  useEffect(() => observe(), [observe, items.length]);

  if (!isCoworkLive()) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center px-6 text-center text-sm text-gray-500">
        Agents aren&apos;t connected in this environment.
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-[1600px] p-4 md:p-6 lg:p-8">
      <header className="mb-5">
        <h1 className="text-[24px] text-[#101828]" style={{ fontWeight: 700 }}>{heading}</h1>
        <p className="mt-1 text-[13px] text-[#667085]">{subheading}</p>
        {campaignId && (
          <button
            type="button"
            onClick={() => { const n = new URLSearchParams(params); n.delete('campaign'); setParams(n, { replace: true }); }}
            className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-[#be43ff]/8 px-3 py-1 text-[12px] text-[#be43ff] transition-colors hover:bg-[#be43ff]/15"
          >
            Scoped to one campaign · show everything
          </button>
        )}
      </header>

      {/* Filters — counts come from the whole library, not the loaded page. */}
      <div className="mb-5 flex flex-wrap items-center gap-2">
        {KINDS.map((k) => {
          const n = k.id === 'all' ? total : counts[k.id] ?? 0;
          if (k.id !== 'all' && n === 0) return null;
          const active = kind === k.id;
          const Icon = k.icon;
          return (
            <button
              key={k.id}
              type="button"
              onClick={() => setKind(k.id)}
              className={cn(
                'flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-[12px] transition-colors',
                active
                  ? 'border-[#be43ff] bg-[#be43ff]/8 text-[#be43ff]'
                  : 'border-gray-200 text-[#475467] hover:bg-gray-50',
              )}
            >
              <Icon className="h-3.5 w-3.5" aria-hidden />
              {k.label}
              <span className={cn('tabular-nums', active ? 'text-[#be43ff]' : 'text-[#98a2b3]')}>{n}</span>
            </button>
          );
        })}

        <div className="relative ml-auto">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" aria-hidden />
          <input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search deliverables…"
            aria-label="Search deliverables"
            className="w-56 rounded-full border border-gray-200 py-1.5 pl-8 pr-3 text-[12px] outline-none placeholder:text-[#98a2b3] focus:border-[#be43ff]"
          />
        </div>
      </div>

      {query.isLoading ? (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
          {Array.from({ length: 10 }).map((_, i) => (
            <div key={i} className="aspect-[4/3] animate-pulse rounded-[16px] bg-gray-100" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <div className="flex min-h-[40vh] flex-col items-center justify-center gap-2 text-center">
          <File className="h-8 w-8 text-gray-300" aria-hidden />
          <p className="text-sm text-[#344054]">
            {search || kind !== 'all' ? 'Nothing matches that filter.' : 'No deliverables yet.'}
          </p>
          <p className="max-w-sm text-xs text-[#98a2b3]">
            Ask an agent for a deck, a report or a visual and it will land here.
          </p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
            {items.map((d) => <DeliverableCard key={d.id} item={d} onOpenDeck={setOpenDeckId} />)}
          </div>
          <div ref={sentinel} className="flex justify-center py-8">
            {isFetchingNextPage && <Loader2 className="h-5 w-5 animate-spin text-[#be43ff]" aria-hidden />}
          </div>
        </>
      )}

      {openDeckId && (
        <PresentationModal
          presentationId={openDeckId}
          onClose={() => setOpenDeckId(null)}
          onEdit={(id) => { setOpenDeckId(null); navigate(`/agents/reports?edit=${encodeURIComponent(id)}`); }}
        />
      )}
    </div>
  );
}
